import { ModalBase } from "../ModalBase";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { closeCurrentModal } from "store/modal/slice";
import { AssetResponse } from "@switcherstudio/api-core-client";
import styles from "./index.module.scss";
import { StyledCheckboxOrRadio } from "components/inputs/checkbox/StyledCheckboxOrRadio";

interface DeleteAssetsModalProps {
    selectedAssets: AssetResponse[];
    relatedRawFiles: AssetResponse[];
    relatedAssets: AssetResponse[];
    deleteAssets: (assets: AssetResponse[]) => Promise<void>;
    sourceTab?: "all-assets" | "raw-files";
}

export const DeleteAssetsModal = ({
    selectedAssets,
    relatedRawFiles,
    relatedAssets,
    deleteAssets,
    sourceTab
}: DeleteAssetsModalProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [open, setOpen] = useState(true);
    const [deleteRelatedRawFiles, setDeleteRelatedRawFiles] = useState(false);
    const [loading, setLoading] = useState(false);

    const close = useCallback(() => {
        setOpen(false);
        dispatch(closeCurrentModal());
    }, [dispatch]);

    const handleDelete = useCallback(async () => {
        setLoading(true);
        const assetsToDelete = [...selectedAssets];

        if (sourceTab === "all-assets" && deleteRelatedRawFiles) {
            // When deleting from all-assets tab also delete related raw files
            // *if user has opted to do so by checking the checkbox*
            assetsToDelete.push(...relatedRawFiles);
        }

        if (sourceTab === "raw-files") {
            // When deleting from raw-files tab, also delete related assets
            assetsToDelete.push(...relatedAssets);
        }

        await deleteAssets(assetsToDelete);
        close();
        setLoading(false);
    }, [
        selectedAssets,
        relatedRawFiles,
        deleteRelatedRawFiles,
        deleteAssets,
        close,
        relatedAssets,
        sourceTab
    ]);

    const confirmationCopy = useMemo(() => {
        const isMultiple = selectedAssets.length > 1;

        switch (sourceTab) {
            case "all-assets":
                return isMultiple
                    ? t("cloud-assets:delete-assets-confirmation")
                    : t("cloud-assets:delete-asset-confirmation");
            case "raw-files":
                const baseCopy = isMultiple
                    ? t("cloud-assets:delete-raws-confirmation")
                    : t("cloud-assets:delete-raw-confirmation");

                let relatedAssetsWarning = "";

                if (relatedAssets.length > 0) {
                    relatedAssetsWarning = isMultiple
                        ? t(
                              "cloud-assets:remove-related-assets-warning-multiple"
                          )
                        : t(
                              "cloud-assets:remove-related-assets-warning-single"
                          );
                }

                return baseCopy + " " + relatedAssetsWarning;
        }
    }, [t, selectedAssets, sourceTab, relatedAssets]);

    return (
        <ModalBase
            isOpen={open}
            setIsOpen={close}
            header={t("shared:attention")}
            primaryButton={{
                text: t("buttons:confirm"),
                onClick: async () => handleDelete()
            }}
            secondaryButton={{
                text: t("buttons:cancel"),
                onClick: () => close()
            }}
            loading={loading}
        >
            <div className={styles["delete-assets-modal"]}>
                <p>{confirmationCopy}</p>
                {sourceTab === "all-assets" && relatedRawFiles.length > 0 && (
                    <>
                        <label
                            htmlFor="delete-related-raw-files"
                            className={styles["related-raw-files-label"]}
                        >
                            <StyledCheckboxOrRadio
                                type="checkbox"
                                id="delete-related-raw-files"
                                checked={deleteRelatedRawFiles}
                                onChange={(e) =>
                                    setDeleteRelatedRawFiles(e.target.checked)
                                }
                            />
                            {t("cloud-assets:delete-related-raw-files")}
                        </label>
                        <ul>
                            {relatedRawFiles.map((file) => (
                                <li key={file.Details.Id}>
                                    <strong>
                                        {t("cloud-assets:raw-file")}:
                                    </strong>{" "}
                                    {file.Details.Name}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {sourceTab === "raw-files" && relatedAssets.length > 0 && (
                    <ul>
                        {relatedAssets.map((file) => (
                            <li key={file.Details.Id}>
                                <strong>{t("cloud-assets:asset")}:</strong>{" "}
                                {file.Details.Name}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </ModalBase>
    );
};
